import React, { useState } from "react";
import { useDispatch } from "react-redux";
import * as actions from "../../../actions";
import Dropdown from "../../../components/dropdown";
import { Popup } from "../../../components/pop-up";

const GRADIENTS = [
  "from-blue-500 to-blue-700",
  "from-orange-400 to-orange-600",
  "from-emerald-500 to-emerald-700",
  "from-violet-500 to-violet-700",
  "from-rose-500 to-rose-700",
];

function initialsGradient(name) {
  let hash = 0;
  for (const c of name) hash = (hash * 31 + c.charCodeAt(0)) >>> 0;
  return GRADIENTS[hash % GRADIENTS.length];
}

function initials(name) {
  return name.split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
}

export default function AppTile({ app, isSuperAdmin, onEdit }) {
  const dispatch = useDispatch();
  const [confirm, setConfirm] = useState(null); // 'disable' | 'enable' | 'delete' | 'rotate' | null
  const isDisabled = app.status === "disabled";
  // Defense-in-depth: only allow http(s) home URLs to neutralise javascript:/data: schemes
  // if an admin ever enters one or the backend ever fails to validate.
  const safeHomeUrl = /^https?:\/\//i.test(app.home_url || "") ? app.home_url : "#";

  const tileBase = isDisabled
    ? "bg-slate-50 border-slate-200 border-dashed"
    : "bg-white border-slate-200 hover:border-slate-300 hover:shadow-md";

  return (
    <div className={`group rounded-lg border p-4 transition-all flex flex-col ${tileBase}`}>
      <div className="flex items-start justify-between mb-3">
        {app.logo_url ? (
          <img src={app.logo_url} alt="" className="h-11 w-11 rounded-lg object-cover" />
        ) : (
          <div className={`h-11 w-11 rounded-lg bg-gradient-to-br ${initialsGradient(app.name)} flex items-center justify-center text-white font-bold text-sm shadow-sm ${isDisabled ? "opacity-60 grayscale" : ""}`}>
            {initials(app.name)}
          </div>
        )}
        {isSuperAdmin && (
          <Dropdown
            trigger={
              <button className="text-slate-400 hover:text-slate-700 hover:bg-slate-100 p-1 rounded -mr-1 -mt-1">
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20"><path d="M10 3a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Zm0 5.5a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Zm0 5.5a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Z"/></svg>
              </button>
            }
            items={[
              <button className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm" onClick={onEdit}>Edit</button>,
              app.requires_auth ? (
                <button className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm" onClick={() => setConfirm('rotate')}>Rotate Secret</button>
              ) : null,
              isDisabled ? (
                <button className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm" onClick={() => setConfirm('enable')}>Enable</button>
              ) : (
                <button className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm" onClick={() => setConfirm('disable')}>Disable</button>
              ),
              isDisabled && (
                <button className="w-full text-left px-3 py-2 hover:bg-red-50 text-red-600 text-sm" onClick={() => setConfirm('delete')}>Delete</button>
              ),
            ].filter(Boolean)}
          />
        )}
      </div>
      <h3 className={`text-sm font-semibold mb-1 leading-snug ${isDisabled ? "text-slate-600" : "text-slate-900"}`}>{app.name}</h3>
      <p className={`text-xs leading-relaxed mb-3 line-clamp-2 ${isDisabled ? "text-slate-400" : "text-slate-500"}`}>
        {app.description || "No description"}
      </p>
      <div className="flex-1" />
      <div className={`flex items-center justify-between pt-3 border-t ${isDisabled ? "border-slate-200" : "border-slate-100"}`}>
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-1.5 text-[11px] font-medium ${isDisabled ? "text-slate-500" : "text-emerald-700"}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${isDisabled ? "bg-slate-400" : "bg-emerald-500"}`}></span>
            {isDisabled ? "Disabled" : "Active"}
          </span>
          {app.requires_auth && (
            <span className="inline-flex items-center rounded-full bg-blue-50 text-blue-700 text-[10px] font-semibold px-1.5 py-0.5">
              SSO
            </span>
          )}
        </div>
        {isDisabled ? (
          <button disabled className="inline-flex items-center gap-1 bg-slate-300 text-slate-500 text-xs font-semibold px-3 py-1.5 rounded cursor-not-allowed">
            Launch
          </button>
        ) : (
          <a href={safeHomeUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold px-3 py-1.5 rounded transition-colors">
            Launch
            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 19.5L19.5 4.5m0 0h-11.25M19.5 4.5v11.25" /></svg>
          </a>
        )}
      </div>

      {/* Confirmation dialogs */}
      {confirm === 'disable' && (
        <Popup
          modalState={[confirm, setConfirm]}
          onConfirm={() => { dispatch(actions.DISABLE_APP_REQ({ id: app.id })); setConfirm(null); }}
          content={<p className="mb-4">Disable "{app.name}"? Existing sessions continue until tokens expire.</p>}
        />
      )}
      {confirm === 'enable' && (
        <Popup
          modalState={[confirm, setConfirm]}
          onConfirm={() => { dispatch(actions.ENABLE_APP_REQ({ id: app.id })); setConfirm(null); }}
          content={<p className="mb-4">Enable "{app.name}"?</p>}
        />
      )}
      {confirm === 'delete' && (
        <Popup
          modalState={[confirm, setConfirm]}
          onConfirm={() => { dispatch(actions.DELETE_APP_REQ({ id: app.id })); setConfirm(null); }}
          content={<p className="mb-4">Permanently delete "{app.name}"? This cannot be undone.</p>}
        />
      )}
      {confirm === 'rotate' && (
        <Popup
          modalState={[confirm, setConfirm]}
          onConfirm={() => { dispatch(actions.ROTATE_APP_SECRET_REQ({ id: app.id, client_id: app.client_id, appName: app.name })); setConfirm(null); }}
          content={<p className="mb-4">Rotate the secret for "{app.name}"? The current secret will be invalidated immediately.</p>}
        />
      )}
    </div>
  );
}
